import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs';
import {
  CardActionsComponent,
  CardBodyComponent,
  CardComponent,
  CardFooterAction,
  CardHeaderComponent,
  CardTitleBadge,
  GroundworksTableComponent,
  GroundworksTableData,
  StateMessageComponent,
  TableGroup,
  TermValueComponent,
  type TermValueItem,
} from '@groundworks/ngx-groundworks-ui';
import {ConfigService} from '../../config.service';
import {ProjectTypeConstants} from '../../constants/project-type.constants';
import {AddressInfo, BoundProductInfo, JobInfo, TaxInfo} from '../../sdk/sdk';
import {ModalService} from '../../services/modal.service';
import {aggregateBoundProducts} from '../../utils/aggregate-bound-products';
import {buildProductTableData} from '../../utils/build-product-table-data';

const TAX_COLUMNS: GroundworksTableData['columns'] = [
  {label: 'Taxes', dataType: 'text'},
  {label: 'Total', dataType: 'currency'},
];

@Component({
  selector: 'app-job-details',
  imports: [
    CardComponent,
    CardBodyComponent,
    CardActionsComponent,
    CardHeaderComponent,
    GroundworksTableComponent,
    StateMessageComponent,
    TermValueComponent,
  ],
  templateUrl: './job-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobDetailsComponent {

  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(ModalService);
  readonly configService = inject(ConfigService);

  readonly job = input<JobInfo | undefined>(undefined);
  readonly address = input<AddressInfo | undefined>(undefined);
  readonly isLoadingInput = input<boolean>(false);
  readonly hasErrorInput = input<boolean>(false);

  readonly cancelJob = output<void>();
  readonly addChangeOrder = output<void>();

  readonly isJobCancelled = computed(() => this.job()?.cancelledAt != null);
  readonly hasChangeOrders = computed(() => (this.job()?.changeOrders?.length ?? 0) > 0);
  // NonInstallableServices projects are excluded from this gate (user-directed). Gating on them
  // works the wrong way round: they can remain open indefinitely (no install date/checkpoint flow
  // drives their completion), and an open project makes every() false, which leaves Add Change
  // Order ENABLED forever on any job carrying one. Filtering them out lets the button disable once
  // the installable work is done, which is the intended behaviour.
  // Note the edge: a job made up ONLY of non-installable projects filters down to an empty list,
  // so length > 0 fails and the button stays enabled even after everything completes.
  // areAnyProjectsCompleted deliberately does NOT apply this filter.
  readonly areAllProjectsCompleted = computed(() => {
    const projects = (this.job()?.projects ?? [])
      .filter(p => p.projectTypeId !== ProjectTypeConstants.NonInstallableServices);
    return projects.length > 0 && projects.every(p => p.completionDate != null);
  });
  readonly areAnyProjectsCompleted = computed(() => {
    const projects = this.job()?.projects;
    return (projects?.length ?? 0) > 0 && projects!.some(p => p.completionDate != null);
  });

  get jobActions(): CardFooterAction[] {
    return [
      {
        label: 'Cancel Job',
        type: 'outline-danger',
        action: this.onCancelJob.bind(this),
        disabled: this.isJobCancelled() || this.areAnyProjectsCompleted() || this.job() == null,
      },
      {
        label: 'View Change Order History',
        type: 'outline-secondary',
        action: this.onViewChangeOrderHistory.bind(this),
        disabled: !this.hasChangeOrders() || this.isJobCancelled() || this.job() == null,
      },
      {
        label: 'Add Change Order',
        type: 'secondary',
        action: this.onAddChangeOrder.bind(this),
        disabled: this.isJobCancelled() || this.areAllProjectsCompleted() || this.job() == null,
      },
    ];
  }

  onCancelJob(): void {
    const jobId = this.job()?.id;
    if (!jobId) return;
    this.modalService.openJobCancelModal('Cancel Job', jobId)
      .closed
      .pipe(
        switchMap(() => this.modalService.openJobCancelSuccessModal().closed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.cancelJob.emit());
  }

  onAddChangeOrder(): void {
    const job = this.job();
    if (!job) return;

    const jobId = job.id;

    const installable = job.installableProductNetTotal;
    const nonInstallable = job.nonInstallableProductNetTotal;
    const recurring = job.recurringProductNetTotal;

    if (installable == null && nonInstallable == null && recurring == null) {
      console.warn('All product net totals are undefined for job:', jobId);
    }

    const currentJobTotal = (installable ?? 0) + (nonInstallable ?? 0) + (recurring ?? 0);

    const taxes = Array.from(job.taxes ?? []).map((t: TaxInfo) => ({
      taxTypeId: t.taxTypeId,
      taxTypeName: t.taxTypeName,
      rate: t.rate,
      amount: t.amount,
      originalAmount: t.amount,
    }));

    this.modalService.openChangeOrderModal(jobId, currentJobTotal, job.branchId, this.address()?.postalCode, taxes)
      .closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.addChangeOrder.emit());
  }

  onViewChangeOrderHistory(): void {
    const jobId = this.job()?.id;
    if (!jobId) return;
    this.modalService.openChangeOrderHistoryModal(jobId);
  }

  readonly state = computed(() => {
    if (this.isLoadingInput()) return 'loading';
    if (this.hasErrorInput()) return 'error';
    if (!this.job()) return 'no-matches';
    return 'matches';
  });


  readonly headerBadge = computed<CardTitleBadge | null>(() => {
    if (!this.isJobCancelled()) return null;
    return {label: 'Cancelled', type: 'danger', icon: 'bi bi-x-circle', size: 'large'};
  });

  readonly overviewBcJobNumber = computed(() => {
    const j = this.job();
    return j?.bcJobNumber == null ? '—' : String(j.bcJobNumber);
  });

  readonly overviewBranchName = computed(() => this.job()?.branchName ?? '—');

  readonly overviewContractDate = computed(() => {
    const j = this.job();
    if (!j?.soldAt) return '—';
    return new Date(j.soldAt).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
  });

  readonly overviewSalesRep = computed(() => this.job()?.inspectedByName ?? '—');

  readonly overviewInsideSalesRep = computed(() => {
    const j = this.job();
    if (!j) return '—';
    return j.soldByName && j.inspectedByName && j.soldByName === j.inspectedByName ? '—' : (j.soldByName ?? '—');
  });

  readonly jobDetails = computed<TermValueItem[]>(() => [
    {term: 'BC Job Number', value: this.overviewBcJobNumber()},
    {term: 'Branch', value: this.overviewBranchName()},
    {term: 'Contract Date', value: this.overviewContractDate()},
    {term: 'Sales Rep', value: this.overviewSalesRep()},
    {term: 'Inside Sales Rep', value: this.overviewInsideSalesRep()},
  ]);

  private readonly aggregatedInstallableProducts = computed(() =>
    aggregateBoundProducts(this.job()?.installableProducts ?? [])
  );
  private readonly aggregatedNonInstallableProducts = computed(() =>
    aggregateBoundProducts(this.job()?.nonInstallableProducts ?? [])
  );
  private readonly aggregatedRecurringProducts = computed(() =>
    aggregateBoundProducts(this.job()?.recurringProducts ?? [])
  );

  readonly installableTableData = computed(() => {
    const job = this.job();
    return this.buildCategoryTableData('Installable Product', this.aggregatedInstallableProducts(),
      job?.installableProductGrossTotal, job?.installableProductNetTotal);
  });

  readonly nonInstallableTableData = computed(() => {
    const job = this.job();
    return this.buildCategoryTableData('Non-Installable Product', this.aggregatedNonInstallableProducts(),
      job?.nonInstallableProductGrossTotal, job?.nonInstallableProductNetTotal);
  });

  readonly recurringTableData = computed(() => {
    const job = this.job();
    return this.buildCategoryTableData('Recurring Product', this.aggregatedRecurringProducts(),
      job?.recurringProductGrossTotal, job?.recurringProductNetTotal);
  });

  readonly taxTableData = computed(() => this.buildTaxTableData(this.job()?.taxes));

  readonly hasNonInstallableProducts = computed(() => this.aggregatedNonInstallableProducts().length > 0);
  readonly hasRecurringProducts = computed(() => this.aggregatedRecurringProducts().length > 0);
  readonly hasTax = computed(() => (this.job()?.taxes?.length ?? 0) > 0);

  /** True when the right column (non-installable, recurring, tax) has any content. */
  readonly hasRightColumnContent = computed(
    () => this.hasNonInstallableProducts() || this.hasRecurringProducts() || this.hasTax()
  );

  private sumAmount(products: BoundProductInfo[], key: 'grossAmount' | 'netAmount'): number {
    return products.reduce((sum, p) => sum + (p[key] ?? 0), 0);
  }

  private buildCategoryTableData(
    label: string,
    products: BoundProductInfo[],
    jobGrossTotal: number | undefined,
    jobNetTotal: number | undefined
  ): GroundworksTableData {
    return buildProductTableData(
      label,
      products,
      jobGrossTotal ?? this.sumAmount(products, 'grossAmount'),
      jobNetTotal ?? this.sumAmount(products, 'netAmount')
    );
  }

  private buildTaxTableData(taxes: TaxInfo[] | undefined): GroundworksTableData | null {
    if (!taxes || taxes.length === 0) return null;
    const rows: TableGroup['rows'] = [
      ...taxes.map((tax) => ({
        type: 'data' as const,
        cells: [
          {value: tax.taxTypeName ?? 'Tax'},
          {value: tax.amount ?? 0},
        ],
      })),
      {
        type: 'total',
        cells: [
          {value: 'Total Tax', isHeader: true, className: 'fw-bold bg-light'},
          {value: taxes.reduce((sum, t) => sum + (t.amount ?? 0), 0), isHeader: true, className: 'fw-bold bg-light'},
        ],
      },
    ];
    return {columns: TAX_COLUMNS, groups: [{label: '', rows}]};
  }
}
